import { useState, useEffect, useCallback, Fragment } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { format, subDays } from 'date-fns';
import { CalendarIcon, Zap, Flame, Droplets, Minus, ChevronDown, ChevronUp, AlertTriangle, Wrench } from 'lucide-react';
import { Input } from '../components/ui/input';
import { PortfolioFilter } from '../components/PortfolioFilter';

const fmtC = v => v != null && v > 0 ? `₹${Math.round(Number(v)).toLocaleString('en-IN')}` : '—';
const fmtN = v => v != null ? Math.round(Number(v)).toLocaleString('en-IN') : '—';

const METER_INFO = {
  electricity: { label: 'Electricity', icon: Zap, unit: 'kWh', color: '#D4A017' },
  gas: { label: 'Gas / Fuel', icon: Flame, unit: 'm3', color: '#C45A41' },
  water: { label: 'Water', icon: Droplets, unit: 'kL', color: '#2D6A4F' },
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function UtilitiesModule() {
  const { getHeaders, API } = useAuth();
  const [tab, setTab] = useState('daily');
  const [date, setDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [dailyData, setDailyData] = useState(null);
  const [mtdData, setMtdData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mtdMonth, setMtdMonth] = useState(new Date().getMonth() + 1);
  const [mtdYear, setMtdYear] = useState(new Date().getFullYear());
  const [filterOutlet, setFilterOutlet] = useState('all');
  const [expandedMtd, setExpandedMtd] = useState(null);
  const [editingBudget, setEditingBudget] = useState({});
  const [savingBudget, setSavingBudget] = useState(null);
  const [portfolioId, setPortfolioId] = useState('');
  const pfParam = portfolioId ? `&portfolio_id=${portfolioId}` : '';

  const saveBudget = async (outletId) => {
    const val = parseFloat(editingBudget[outletId]) || 0;
    setSavingBudget(outletId);
    try {
      await axios.put(`${API}/admin/maintenance-budget/${outletId}`, {
        year: mtdYear, month: mtdMonth, budget: val
      }, { headers: getHeaders() });
      toast.success('Budget saved');
      loadMtd();
    } catch { toast.error('Failed to save budget'); }
    finally { setSavingBudget(null); }
  };

  const loadDaily = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/maintenance-daily?date=${format(date, 'yyyy-MM-dd')}${pfParam}`, { headers: getHeaders() });
      setDailyData(res.data);
    } catch { toast.error('Failed to load daily data'); }
    finally { setLoading(false); }
  }, [API, getHeaders, date, pfParam]);

  const loadMtd = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/maintenance-overview?year=${mtdYear}&month=${mtdMonth}${pfParam}`, { headers: getHeaders() });
      setMtdData(res.data);
    } catch { toast.error('Failed to load MTD data'); }
    finally { setLoading(false); }
  }, [API, getHeaders, mtdYear, mtdMonth, pfParam]);

  useEffect(() => { if (tab === 'daily') loadDaily(); }, [tab, loadDaily]);
  useEffect(() => { if (tab === 'mtd') loadMtd(); }, [tab, loadMtd]);

  const dailyOutlets = dailyData?.outlets || [];
  const mtdOutlets = mtdData?.outlets || [];
  const filteredDaily = filterOutlet === 'all' ? dailyOutlets : dailyOutlets.filter(o => o.outlet_id === filterOutlet);
  const filteredMtd = filterOutlet === 'all' ? mtdOutlets : mtdOutlets.filter(o => o.outlet_id === filterOutlet);
  // For outlet filter dropdown, show outlets from whichever dataset is loaded
  const allOutlets = tab === 'daily' ? dailyOutlets : mtdOutlets;

  return (
    <div data-testid="admin-maintenance-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-xl sm:text-2xl tracking-tight font-semibold text-[#1A2421]">Maintenance & Energy</h2>
            <p className="text-sm text-[#5C6B64] mt-1">Utility consumption & costs across all outlets</p>
          </div>
          <PortfolioFilter value={portfolioId} onChange={setPortfolioId} />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <TabsList className="bg-[#F3F5F2] p-0.5 rounded-lg w-fit">
            <TabsTrigger value="daily" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white text-sm px-4"
              data-testid="maint-admin-daily-tab">Daily</TabsTrigger>
            <TabsTrigger value="mtd" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white text-sm px-4"
              data-testid="maint-admin-mtd-tab">MTD</TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Outlet filter */}
            <Select value={filterOutlet} onValueChange={setFilterOutlet}>
              <SelectTrigger className="w-36 h-8 border-[#E2E8DF] text-sm" data-testid="maint-outlet-filter">
                <SelectValue placeholder="All Outlets" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Outlets</SelectItem>
                {allOutlets.map(o => <SelectItem key={o.outlet_id} value={o.outlet_id}>{o.outlet_name}</SelectItem>)}
              </SelectContent>
            </Select>

            {tab === 'daily' && (
              <>
                <Button variant="outline" size="sm"
                  onClick={() => setDate(subDays(new Date(), 1))}
                  className={`h-8 text-xs border-[#E2E8DF] ${
                    format(date, 'yyyy-MM-dd') === format(subDays(new Date(), 1), 'yyyy-MM-dd')
                      ? 'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)]' : 'text-[#5C6B64] hover:bg-[#F3F5F2]'
                  }`}
                  data-testid="maint-admin-yesterday-btn">
                  Yesterday
                </Button>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs border-[#E2E8DF] text-[#5C6B64] gap-1"
                      data-testid="maint-admin-date-picker">
                      <CalendarIcon className="w-3.5 h-3.5" />
                      {format(date, 'dd MMM yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar mode="single" selected={date}
                      onSelect={d => { if (d) { setDate(d); setCalendarOpen(false); } }}
                      disabled={d => d > new Date()} />
                  </PopoverContent>
                </Popover>
              </>
            )}

            {tab === 'mtd' && (
              <>
                <Select value={String(mtdMonth)} onValueChange={v => setMtdMonth(parseInt(v))}>
                  <SelectTrigger className="w-20 h-8 border-[#E2E8DF] text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={String(mtdYear)} onValueChange={v => setMtdYear(parseInt(v))}>
                  <SelectTrigger className="w-20 h-8 border-[#E2E8DF] text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
          </div>
        )}

        {/* ─── DAILY VIEW ─── */}
        <TabsContent value="daily">
          {!loading && dailyData && (
            <Card className="border-[#E2E8DF] shadow-sm overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left text-[#1A2421]">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-[0.05em] text-[#5C6B64] bg-[#F9F9F8] border-b border-[#E2E8DF]">
                        <th className="px-3 py-2.5 font-medium sticky left-0 bg-[#F9F9F8] z-10 min-w-[120px]">Outlet</th>
                        {Object.entries(METER_INFO).map(([key, info]) => (
                          <th key={key} colSpan={4} className="px-0 py-1 font-medium text-center border-l border-[#E2E8DF]">
                            <div className="flex items-center justify-center gap-1 py-1">
                              <info.icon className="w-3 h-3" style={{ color: info.color }} strokeWidth={2} />
                              {info.label}
                            </div>
                          </th>
                        ))}
                      </tr>
                      <tr className="text-[10px] uppercase tracking-[0.05em] text-[#5C6B64] bg-[#F9F9F8] border-b border-[#E2E8DF]">
                        <th className="px-3 py-1.5 font-medium sticky left-0 bg-[#F9F9F8] z-10"></th>
                        {Object.keys(METER_INFO).map(key => (
                          <Fragment key={key}>
                            <th className="px-2 py-1.5 font-medium text-right border-l border-[#E2E8DF]">Open</th>
                            <th className="px-2 py-1.5 font-medium text-right">Close</th>
                            <th className="px-2 py-1.5 font-medium text-right">Units</th>
                            <th className="px-2 py-1.5 font-medium text-right">Cost</th>
                          </Fragment>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDaily.map((o, i) => (
                        <tr key={o.outlet_id}
                          className={`border-b border-[#E2E8DF] ${!o.has_data ? 'opacity-50' : ''} ${i % 2 === 0 ? '' : 'bg-[#F9F9F8]/50'}`}
                          data-testid={`daily-row-${o.outlet_id}`}>
                          <td className="px-3 py-2.5 font-medium sticky left-0 bg-white z-10 whitespace-nowrap">
                            {o.outlet_name}
                            {!o.has_data && <span className="ml-1.5 text-[10px] text-[#9CA3AF] italic">No data</span>}
                          </td>
                          {Object.entries(METER_INFO).map(([key, info]) => {
                            const m = o.meters?.[key];
                            if (!m) {
                              return (
                                <Fragment key={key}>
                                  <td className="px-2 py-2.5 text-right text-[#9CA3AF] border-l border-[#E2E8DF]"><Minus className="w-3 h-3 inline" /></td>
                                  <td className="px-2 py-2.5 text-right text-[#9CA3AF]"><Minus className="w-3 h-3 inline" /></td>
                                  <td className="px-2 py-2.5 text-right text-[#9CA3AF]"><Minus className="w-3 h-3 inline" /></td>
                                  <td className="px-2 py-2.5 text-right text-[#9CA3AF]"><Minus className="w-3 h-3 inline" /></td>
                                </Fragment>
                              );
                            }
                            const unitLabel = m.unit || info.unit;
                            return (
                              <Fragment key={key}>
                                <td className="px-2 py-2.5 text-right border-l border-[#E2E8DF]">{m.opening != null ? fmtN(m.opening) : '—'}</td>
                                <td className="px-2 py-2.5 text-right">{m.closing != null ? fmtN(m.closing) : '—'}</td>
                                <td className="px-2 py-2.5 text-right font-medium">{fmtN(m.consumption)} <span className="text-[10px] text-[#5C6B64]">{unitLabel}</span></td>
                                <td className="px-2 py-2.5 text-right font-medium">{fmtC(m.cost)}</td>
                              </Fragment>
                            );
                          })}
                        </tr>
                      ))}
                      {filteredDaily.length === 0 && (
                        <tr><td colSpan={13} className="text-center py-8 text-[#5C6B64]">No outlets found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ─── MTD VIEW ─── */}
        <TabsContent value="mtd">
          {!loading && mtdData && (
            <>
              <div className="flex items-center gap-2 mb-4">
                <Badge variant="outline" className="border-[#E2E8DF] text-[#5C6B64] text-xs">
                  Data through {mtdData.data_through} · {mtdData.days_in_month} days in month
                </Badge>
              </div>

              <div className="space-y-2">
                {filteredMtd.map(o => {
                  const hasAnyData = Object.values(o.meters || {}).some(m => m && m.days_with_data > 0);
                  const isExpanded = expandedMtd === o.outlet_id;
                  const totalCost = Object.values(o.meters || {}).reduce((s, m) => s + ((m && m.total_cost) || 0), 0);
                  const totalConsumption = Object.entries(o.meters || {}).filter(([, m]) => m && m.days_with_data > 0)
                    .map(([k, m]) => `${fmtN(m.total_consumption)} ${METER_INFO[k]?.unit || ''}`);
                  const bt = o.budget_tracking;
                  const overBudget = bt && bt.budget > 0 && bt.pct_used > 100;

                  return (
                  <Card key={o.outlet_id}
                    className={`border-[#E2E8DF] shadow-sm overflow-hidden ${!hasAnyData ? 'opacity-50' : ''} ${overBudget ? 'border-[#C45A41]/30' : ''}`}
                    data-testid={`mtd-card-${o.outlet_id}`}>
                    <div
                      className={`flex items-center justify-between px-4 py-3 cursor-pointer select-none ${hasAnyData ? 'hover:bg-[#F9F9F8]/60' : ''}`}
                      onClick={() => hasAnyData && setExpandedMtd(isExpanded ? null : o.outlet_id)}
                      data-testid={`mtd-toggle-${o.outlet_id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-sm font-semibold text-[#1A2421] truncate">{o.outlet_name}</span>
                        {!hasAnyData && <span className="text-[10px] text-[#9CA3AF] italic">No data</span>}
                        {overBudget && (
                          <span className="flex items-center gap-1 text-[10px] text-[#C45A41] font-medium">
                            <AlertTriangle className="w-3 h-3" />Over Budget
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {hasAnyData && (
                          <>
                            {bt && bt.budget > 0 && (
                              <div className="text-right hidden sm:block">
                                <p className="text-[10px] text-[#5C6B64] uppercase">Budget Used</p>
                                <p className={`text-sm font-semibold ${bt.pct_used > 100 ? 'text-[#C45A41]' : bt.pct_used > 80 ? 'text-[#D4A017]' : 'text-[#2D6A4F]'}`}>{bt.pct_used}%</p>
                              </div>
                            )}
                            <div className="text-right hidden sm:block">
                              <p className="text-[10px] text-[#5C6B64] uppercase">Total Spend</p>
                              <p className="text-sm font-semibold text-[#1A2421]">{fmtC(bt?.total_spend || totalCost)}</p>
                            </div>
                            <div className="text-right sm:hidden">
                              <p className="text-sm font-semibold text-[#1A2421]">{fmtC(bt?.total_spend || totalCost)}</p>
                            </div>
                            {!isExpanded && totalConsumption.length > 0 && (
                              <div className="hidden sm:flex items-center gap-1.5">
                                {totalConsumption.map((t, i) => (
                                  <Badge key={i} variant="outline" className="text-[10px] border-[#E2E8DF] text-[#5C6B64]">{t}</Badge>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                        {hasAnyData && (isExpanded
                          ? <ChevronUp className="w-4 h-4 text-[#5C6B64]" />
                          : <ChevronDown className="w-4 h-4 text-[#5C6B64]" />
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <CardContent className="px-4 pb-4 pt-0 border-t border-[#E2E8DF]">
                        {/* Budget Tracking */}
                        <div className="pt-3 mb-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Wrench className="w-3.5 h-3.5 text-[#5C6B64]" />
                            <span className="text-xs font-semibold text-[#5C6B64] uppercase tracking-wide">Budget Tracking</span>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                            <div className="p-2 bg-white rounded border border-[#E2E8DF]">
                              <p className="text-[10px] text-[#5C6B64] uppercase">Budget</p>
                              <div className="flex items-center gap-1">
                                <Input type="number" placeholder="Set..." className="h-7 text-sm border-[#E2E8DF] w-24"
                                  value={editingBudget[o.outlet_id] ?? (bt?.budget || '')}
                                  onChange={e => setEditingBudget(p => ({ ...p, [o.outlet_id]: e.target.value }))}
                                  data-testid={`budget-input-${o.outlet_id}`} />
                                <Button size="sm" className="h-7 px-2 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white text-xs"
                                  onClick={(e) => { e.stopPropagation(); saveBudget(o.outlet_id); }}
                                  disabled={savingBudget === o.outlet_id}
                                  data-testid={`save-budget-${o.outlet_id}`}>
                                  {savingBudget === o.outlet_id ? '...' : 'Set'}
                                </Button>
                              </div>
                            </div>
                            <div className="p-2 bg-white rounded border border-[#E2E8DF]">
                              <p className="text-[10px] text-[#5C6B64] uppercase">Energy</p>
                              <p className="text-sm font-semibold text-[#1A2421]">{fmtC(bt?.energy_cost)}</p>
                            </div>
                            <div className="p-2 bg-white rounded border border-[#E2E8DF]">
                              <p className="text-[10px] text-[#5C6B64] uppercase">Expenses</p>
                              <p className="text-sm font-semibold text-[#1A2421]">{fmtC(bt?.expenses_cost)}</p>
                            </div>
                            <div className="p-2 bg-white rounded border border-[#E2E8DF]">
                              <p className="text-[10px] text-[#5C6B64] uppercase">Total Spend</p>
                              <p className="text-sm font-bold text-[#1A2421]">{fmtC(bt?.total_spend)}</p>
                            </div>
                            <div className={`p-2 rounded border ${overBudget ? 'bg-[#C45A41]/5 border-[#C45A41]/20' : 'bg-white border-[#E2E8DF]'}`}>
                              <p className="text-[10px] text-[#5C6B64] uppercase">Remaining</p>
                              <p className={`text-sm font-bold ${bt?.remaining < 0 ? 'text-[#C45A41]' : 'text-[#2D6A4F]'}`}>
                                {bt?.budget > 0 ? fmtC(bt.remaining) : '—'}
                              </p>
                            </div>
                          </div>
                          {bt?.budget > 0 && (
                            <div className="mt-2 h-1.5 bg-[#E2E8DF] rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${bt.pct_used > 100 ? 'bg-[#C45A41]' : bt.pct_used > 80 ? 'bg-[#D4A017]' : 'bg-[#2D6A4F]'}`}
                                style={{ width: `${Math.min(bt.pct_used, 100)}%` }} />
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-[#E2E8DF]">
                          {Object.entries(METER_INFO).map(([key, info]) => {
                            const m = o.meters?.[key];
                            if (!m || !m.days_with_data || m.days_with_data === 0) {
                              return (
                                <div key={key} className="p-3 bg-[#F9F9F8] border border-[#E2E8DF] rounded-lg opacity-50">
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <info.icon className="w-4 h-4" style={{ color: info.color }} strokeWidth={1.5} />
                                    <span className="text-xs font-semibold text-[#1A2421]">{info.label}</span>
                                  </div>
                                  <p className="text-xs text-[#9CA3AF] italic">No data</p>
                                </div>
                              );
                            }
                            return (
                              <div key={key} className="p-3 bg-[#F9F9F8] border border-[#E2E8DF] rounded-lg"
                                data-testid={`mtd-meter-${o.outlet_id}-${key}`}>
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-1.5">
                                    <info.icon className="w-4 h-4" style={{ color: info.color }} strokeWidth={1.5} />
                                    <span className="text-xs font-semibold text-[#1A2421]">{info.label}</span>
                                  </div>
                                  <Badge variant="outline" className="text-[10px] border-[#E2E8DF] text-[#5C6B64]">
                                    {m.days_with_data} days
                                  </Badge>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="p-2 bg-white rounded border border-[#E2E8DF]">
                                    <p className="text-[10px] text-[#5C6B64] uppercase">Total</p>
                                    <p className="text-sm font-semibold text-[#1A2421]">{fmtN(m.total_consumption)} <span className="text-[10px] text-[#5C6B64]">{info.unit}</span></p>
                                  </div>
                                  <div className="p-2 bg-white rounded border border-[#E2E8DF]">
                                    <p className="text-[10px] text-[#5C6B64] uppercase">Avg Daily</p>
                                    <p className="text-sm font-semibold text-[#1A2421]">{fmtN(m.avg_daily)} <span className="text-[10px] text-[#5C6B64]">{info.unit}</span></p>
                                  </div>
                                  <div className="p-2 bg-white rounded border border-[#E2E8DF]">
                                    <p className="text-[10px] text-[#5C6B64] uppercase">MTD Cost</p>
                                    <p className="text-sm font-semibold text-[#1A2421]">{fmtC(m.total_cost)}</p>
                                  </div>
                                  <div className="p-2 bg-white rounded border border-[color:var(--color-primary)]/10" style={{ backgroundColor: `${info.color}08` }}>
                                    <p className="text-[10px] text-[#5C6B64] uppercase">Expected Bill</p>
                                    <p className="text-sm font-bold" style={{ color: info.color }}>{fmtC(m.expected_bill)}</p>
                                  </div>
                                </div>
                                {/* Chunk 3 — per-physical-meter breakdown */}
                                {Array.isArray(m.meter_breakdown) && m.meter_breakdown.length > 0 && (
                                  <div className="mt-2 space-y-1" data-testid={`meter-breakdown-${o.outlet_id}-${key}`}>
                                    {m.meter_breakdown.map(mb => {
                                      const mf = (m.meter_forecasts || []).find(f => f.meter_id === mb.meter_id);
                                      return (
                                        <div key={mb.meter_id} className="text-[11px] bg-white/60 rounded px-2 py-1 flex flex-wrap items-center gap-2" data-testid={`meter-row-${mb.meter_id}`}>
                                          <span className="font-medium text-[#1A2421]">{mb.meter_name}</span>
                                          {mb.status && mb.status !== 'active' && <span className="text-[9px] uppercase text-[#C45A41]">· {mb.status}</span>}
                                          <span className="text-[#5C6B64]">{fmtN(mb.consumption)} {mb.unit}</span>
                                          <span className="text-[#5C6B64]">· {fmtC(mb.actual_cost)} actual</span>
                                          {mf && mf.expected_meter_bill > 0 && <span className="text-[#5C6B64]">· {fmtC(mf.expected_meter_bill)} expected</span>}
                                          <span className="text-[9px] text-[#5C6B64] ml-auto">{mb.days_with_data}d</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                  );
                })}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
